import React from "react"
import { useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import PropTypes from "prop-types"
import { Button, message } from "antd"
import { Icon } from "ui/Icon"
import styles from "../WebsiteIntegration.module.css"
import chatbotStyles from "./AiChatbotWidget.module.css"
import useActiveGuide from "hooks/useActiveClient"
import { Text } from "ui/Typography"
import { useTheme } from "context/AntdThemeContext"
import { colors } from "themes/variables"

const StepNumber = ({ number }) => (
  <div className={styles.stepNumber}>
    <p style={{ color: "black", margin: 0 }}>{number}</p>
  </div>
)

const StepSection = ({ number, title, subtitle, children }) => (
  <div className={styles.stepSection}>
    <StepNumber number={number} />
    <div className={styles.stepContent}>
      <div className={chatbotStyles.stepTitle}>
        <p style={{ color: "black", fontWeight: 600, margin: 0 }}>{title}</p>
      </div>
      <p style={{ color: "black", fontSize: "14px" }}>{subtitle}</p>
      <div className={chatbotStyles.stepChildrenWrapper}>{children}</div>
    </div>
  </div>
)

export const AiChatbotWidget = () => {
  const user = useSelector((store) => store.user)
  const [t] = useTranslation()
  const { isActive: active } = useActiveGuide()
  const { isSportsman } = useTheme()

  // Get theme color for button
  const buttonColor = isSportsman ? colors.sportsmanBrand : colors.sportswomanBrand
  const themeName = isSportsman ? "Sportsman" : "Sportswoman"

  // AI Backend URL - uses environment variable or defaults to production
  const aiBackendUrl = process.env.REACT_APP_AI_CHATBOT_BACKEND_URL

  // Self-contained widget embed code - no iframe, calls backend API directly
  const code = `<!-- DS Guide AI Chatbot Widget -->
<script
  src="${aiBackendUrl}/static/ds-chatbot-widget.js"
  data-guide-uuid="${user?.guide?.uuid}"
  data-api-url="${aiBackendUrl}"
  data-primary-color="${buttonColor}">
</script>`

  const handleCopy = () => {
    navigator.clipboard
      .writeText(code)
      .then(() => message.success(t("webIntegration.ai_chatbot_copy_success")))
      .catch(() => message.error(t("webIntegration.ai_chatbot_copy_failure")))
  }

  return (
    <>
      <div className={chatbotStyles.header}>
        <Text fw="600" size="18px">
          {t("webIntegration.ai_chatbot_widget")}
        </Text>
        <Text color="gray" className={chatbotStyles.description}>
          {t("webIntegration.ai_chatbot_description")}
        </Text>
      </div>

      <StepSection
        number={1}
        title={t("webIntegration.ai_chatbot_copy_code_title")}
        subtitle={t("webIntegration.ai_chatbot_copy_code_subtitle")}
      >
        <div className={styles.codeBlockContainer}>
          <pre className={chatbotStyles.codeBlock}>
            {code}
          </pre>
          <div className={styles.codeBlockInner}>
            <Button
              color="primary"
              variant="outlined"
              icon={<Icon type="feather" name="Copy" width={16} height={16} />}
              onClick={active ? handleCopy : undefined}
              disabled={!active}
            >
              {t("webIntegration.ai_chatbot_copy_code_button")}
            </Button>
          </div>
        </div>
      </StepSection>

      <StepSection
        number={2}
        title={t("webIntegration.ai_chatbot_preview_title")}
        subtitle={t("webIntegration.ai_chatbot_preview_subtitle")}
      >
        <div className={chatbotStyles.previewBox}>
          <Text size="14px">
            • <strong>{t("webIntegration.ai_chatbot_label_position")}</strong> {t("webIntegration.ai_chatbot_preview_position")}
            <br />
            • <strong>{t("webIntegration.ai_chatbot_label_icon")}</strong> {t("webIntegration.ai_chatbot_preview_icon")}
            <br />
            • <strong>{t("webIntegration.ai_chatbot_label_chat_window")}</strong> {t("webIntegration.ai_chatbot_preview_chat_window")}
            <br />
            • <strong>{t("webIntegration.ai_chatbot_label_color")}</strong> {t("webIntegration.ai_chatbot_preview_color", { theme: themeName })}
            <br />
            • <strong>{t("webIntegration.ai_chatbot_label_works_anywhere")}</strong> {t("webIntegration.ai_chatbot_preview_works_anywhere")}
          </Text>
        </div>
      </StepSection>

      <StepSection
        number={3}
        title={t("webIntegration.ai_chatbot_customization_title")}
        subtitle={t("webIntegration.ai_chatbot_customization_subtitle")}
      >
        <div className={chatbotStyles.previewBox}>
          <Text size="14px">
            • <code>data-primary-color="#D92C9E"</code> - {t("webIntegration.ai_chatbot_customization_color")}
            <br />
            • <code>data-position="bottom-left"</code> - {t("webIntegration.ai_chatbot_customization_position")}
            <br />
            • <code>data-welcome-message="Hello!"</code> - {t("webIntegration.ai_chatbot_customization_welcome")}
          </Text>
        </div>
      </StepSection>
    </>
  )
}

StepNumber.propTypes = {
  number: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
}

StepSection.propTypes = {
  number: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired
}
